import { useAccount } from "@qw/auth";
import { useTheme } from "@qw/ui";
import {
  capturePageView,
  group,
  identify,
  registerSuperProperties,
  setOperatorProfile,
} from "@third-plane/posthog-integration-interface";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { isPostHogConfigured, resetPostHog } from "./posthog";

const toPostHogDate = (value: unknown) => {
  if (!value) {
    return undefined;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  return String(value);
};

export function PostHogTracker() {
  const { user } = useAccount();
  const location = useLocation();
  const lastCapturedUrlRef = useRef<string | null>(null);
  const { theme, selectedMode, mode } = useTheme();

  const agencyId = user.agency?.id ?? user.agencyId;
  const role = user.roles.length > 0 ? user.roles.join(",") : undefined;
  const accountCreatedAt = toPostHogDate(user.createdAt);
  // Name every logged-in user — staff, agency brokers, AND carriers — for
  // legibility. Landon explicitly approved all three, incl. carriers (2026-08-06).
  // Insureds don't log into qwib. Gated by the lib's allowOperatorIdentification opt-in.
  const operatorName = [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || undefined;
  const operatorEmail = user.email ?? undefined;

  useEffect(() => {
    if (!isPostHogConfigured()) {
      return;
    }

    identify(user.id, {
      role,
      agency: agencyId,
      accountCreatedAt,
    });

    setOperatorProfile({ name: operatorName, email: operatorEmail });

    if (agencyId) {
      group("agency", agencyId, {
        bindHqId: user.agency?.bindHqId ?? undefined,
        isTest: user.agency?.isTest ?? undefined,
      });
    }
  }, [
    accountCreatedAt,
    agencyId,
    operatorEmail,
    operatorName,
    role,
    user.agency?.bindHqId,
    user.agency?.isTest,
    user.id,
  ]);

  // The theme lives only in localStorage, so stamping it on every event is the one way to count
  // who runs dark mode or a non-default color. The wrapper caps person properties at identify()'s
  // fixed traits; read a person's current value with argMax over their latest events.
  useEffect(() => {
    if (!isPostHogConfigured()) {
      return;
    }

    // ThemeProvider migrates the legacy "beef" mode to light mode + beef theme in an effect that runs
    // after this child effect on first mount, so report the migrated values up front.
    const legacyBeefMode = selectedMode === "beef";
    registerSuperProperties({
      theme: legacyBeefMode ? "beef" : theme,
      selectedMode: legacyBeefMode ? "light" : selectedMode,
      resolvedMode: legacyBeefMode ? "light" : mode,
    });
  }, [mode, selectedMode, theme]);

  useEffect(() => {
    return () => {
      resetPostHog();
    };
  }, []);

  useEffect(() => {
    if (!isPostHogConfigured()) {
      return;
    }

    // Only origin + pathname reaches analytics — never location.search/hash.
    // inbox stores free-form search text in URL state (term, search, etc.),
    // which would leak user-entered names/emails as an event property if
    // shipped to PostHog.
    const currentUrl = `${window.location.origin}${location.pathname}`;
    if (lastCapturedUrlRef.current === currentUrl) {
      return;
    }

    lastCapturedUrlRef.current = currentUrl;
    capturePageView({ currentUrl, routeName: location.pathname });
  }, [location.pathname]);

  return null;
}
